import { useEffect, useId, useMemo, useRef } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@rocco/utils/cn';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { Input } from '@rocco/components/input';
import { ScrollArea } from '@rocco/components/scroll-area';
import type { DirectConversation } from '../../types';
import { useConversation } from '../../context';
import { useUpdateConversation } from '../../hooks/use-update-conversation';
import { ChatSettingsButton } from './ChatSettingsButton';
import { ChatSettingsLeaveButton } from './ChatSettingsLeaveButton';
import { ChatSettingsThumbnail } from './ChatSettingsThumbnail';
import { BuildHubSelect } from './BuildHubSelect';

const settingsFormSchema = z.object({
    title: z
        .string()
        .trim()
        .max(100, { message: 'Maximum 100 characters' })
        .transform((value) => value.replace(/\s+/g, ' ')),
    buildHubID: z.string(),
});

/**
 * The "Settings" tab content of the chat settings.
 * @see {@link ChatSettings}
 */
export function ChatSettingsSettings({
    className,
    conversation,
    onClose,
    onLeaveSuccess,
}: {
    className?: string;
    conversation: DirectConversation;
    onClose?: () => void;
    onLeaveSuccess?: () => void;
}) {
    const showLeaveButton =
        conversation.IsGroupChat && conversation.Participants.length > 2;
    const { professionalID } = useConversation();
    const { isSubmitting, updateConversation, leaveConversation } =
        useUpdateConversation({
            conversationID: Number(conversation.ID),
            professionalID,
            onUpdateSuccess: (conversation) => {
                settingsForm.reset({
                    title: conversation.Name ?? '',
                    buildHubID: conversation.BuildHub?.ID ?? '',
                });
                onClose?.();
            },
            onLeaveSuccess,
        });
    const isMounted = useRef(false);
    const settingsFormReactId = useId();
    const settingsFormId = `chat-settings-${settingsFormReactId}`;
    const defaultValues = useMemo(() => {
        return {
            title: conversation.Name ?? '',
            buildHubID: conversation.BuildHub?.ID ?? '',
        };
    }, [conversation]);
    const settingsForm = useForm<z.infer<typeof settingsFormSchema>>({
        resolver: zodResolver(settingsFormSchema),
        mode: 'onChange',
        defaultValues,
        disabled: isSubmitting,
    });

    function onSubmit(values: z.infer<typeof settingsFormSchema>) {
        updateConversation({
            name: values.title,
            buildHubID: values.buildHubID ? Number(values.buildHubID) : 0,
        });
    }

    // Reset form default values when the conversation is updated.
    useEffect(() => {
        if (isMounted.current) {
            settingsForm.reset(defaultValues, { keepDirtyValues: true });
        } else {
            isMounted.current = true;
        }
    }, [settingsForm, defaultValues]);

    return (
        <Form {...settingsForm}>
            <form
                id={settingsFormId}
                className={cn('flex flex-col gap-[8px]', className)}
                onSubmit={settingsForm.handleSubmit(onSubmit)}
            >
                <ScrollArea
                    className="min-h-0 flex-1"
                    scrollBarClassName="w-[6px] p-0"
                    thumbClassName="bg-gray-500"
                    overscroll="contain"
                >
                    <div className="space-y-[28px]">
                        <FormField
                            name="title"
                            control={settingsForm.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel
                                        className="ml-[4px] text-[14px]"
                                        errorVariant="errorDarker"
                                    >
                                        Conversation title
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            className="rounded-[8px] bg-[#f2f2f2] px-[12px] py-[8px] text-[16px]! leading-tight! md:text-[14px]!"
                                            containerClassName="mt-[8px]!"
                                            color="contrast"
                                            text="default"
                                            placeholder="Enter a title for this conversation or group"
                                            autoFocus
                                            autoComplete="off"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage
                                        className="text-[12px]"
                                        variant="darker"
                                    />
                                </FormItem>
                            )}
                        />
                        <FormField
                            name="buildHubID"
                            control={settingsForm.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel
                                        className="ml-[4px] text-[14px]"
                                        errorVariant="errorDarker"
                                    >
                                        Project
                                    </FormLabel>
                                    <FormControl>
                                        <BuildHubSelect
                                            ref={field.ref}
                                            className="mt-[8px]! bg-[#f2f2f2]"
                                            currentBuildHub={
                                                conversation.BuildHub
                                            }
                                            name={field.name}
                                            value={field.value}
                                            disabled={field.disabled}
                                            onValueChange={field.onChange}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <div>
                            <FormLabel
                                className="ml-[4px] text-[14px]"
                                errorVariant="errorDarker"
                            >
                                Thumbnail
                            </FormLabel>
                            <ChatSettingsThumbnail
                                className="mt-[8px]"
                                conversation={conversation}
                                isDisabled={settingsForm.formState.disabled}
                            />
                        </div>
                        {showLeaveButton && (
                            <div>
                                <ChatSettingsLeaveButton
                                    disabled={settingsForm.formState.disabled}
                                    onConfirm={leaveConversation}
                                />
                            </div>
                        )}
                    </div>
                </ScrollArea>
                <div className="flex items-center justify-end gap-[4px]">
                    <ChatSettingsButton
                        className="hover:bg-black/10"
                        type="button"
                        color="transparent"
                        disabled={settingsForm.formState.disabled}
                        onClick={onClose}
                    >
                        Cancel
                    </ChatSettingsButton>
                    <ChatSettingsButton
                        type="submit"
                        disabled={
                            settingsForm.formState.disabled ||
                            !settingsForm.formState.isDirty
                        }
                        isLoading={settingsForm.formState.disabled}
                    >
                        Save & Close
                    </ChatSettingsButton>
                </div>
            </form>
        </Form>
    );
}
